import { Routes } from '@angular/router';
import { lazyFeatureRoute } from './lazy-feature-route';

/**
 * Entity features that are code-split out of the main bundle.
 *
 * Each entry lists the exact paths its <name>.route.ts defines, so the router only fetches a
 * chunk when the URL really belongs to that feature. When you add a route to a lazy feature's
 * .route.ts, add its path here too. Two checks catch drift:
 *   - lazy-features.spec.ts: every path of every .route.ts is claimed by the right entry, in order.
 *   - scripts/check-lazy-features.js (CI job lazy-features-check): each entry's import() loads the
 *     module that registers that same .route.ts.
 */
export const lazyFeatureRoutes: Routes = [
  lazyFeatureRoute(
    ['achievements-collection', 'achievements-collection/add', 'achievements-collection/:id', 'achievements-collection/edit/:id'],
    () => import('./lazy/achievements-collection.module').then((m) => m.AchievementsCollectionFeatureModule),
  ),
  lazyFeatureRoute(
    ['achievements', 'achievements/add', 'achievements/:id', 'achievements/edit/:id'],
    () => import('./lazy/achievements.module').then((m) => m.AchievementsFeatureModule),
  ),
  lazyFeatureRoute(
    ['search'],
    () => import('./lazy/ai-search.module').then((m) => m.AiSearchFeatureModule),
  ),
  lazyFeatureRoute(
    ['audio-collections', 'audio-collections/add', 'audio-collections/:id', 'audio-collections/edit/:id'],
    () => import('./lazy/audio-collections.module').then((m) => m.AudioCollectionsFeatureModule),
  ),
  lazyFeatureRoute(
    ['automations-logs', 'automations-logs/:id'],
    () => import('./lazy/automation-log.module').then((m) => m.AutomationLogFeatureModule),
  ),
  lazyFeatureRoute(
    ['batches', 'batches/add', 'batches/:id', 'batches/edit/:id'],
    () => import('./lazy/batches.module').then((m) => m.BatchesFeatureModule),
  ),
  lazyFeatureRoute(
    ['boost-events', 'boost-events/add', 'boost-events/:id', 'boost-events/edit/:id'],
    () => import('./lazy/boost-events.module').then((m) => m.BoostEventsFeatureModule),
  ),
  lazyFeatureRoute(
    ['byob', 'byob/add', 'byob/:id', 'byob/edit/:id'],
    () => import('./lazy/byob.module').then((m) => m.ByobFeatureModule),
  ),
  lazyFeatureRoute(
    ['categories', 'categories/add', 'categories/:id', 'categories/edit/:id'],
    () => import('./lazy/categories.module').then((m) => m.CategoriesFeatureModule),
  ),
  lazyFeatureRoute(
    ['category-collections', 'category-collections/add', 'category-collections/:id', 'category-collections/edit/:id'],
    () => import('./lazy/category-collections.module').then((m) => m.CategoryCollectionsFeatureModule),
  ),
  lazyFeatureRoute(
    ['challenge-collections', 'challenge-collections/add', 'challenge-collections/:id', 'challenge-collections/edit/:id'],
    () => import('./lazy/challenge-collections.module').then((m) => m.ChallengeCollectionsFeatureModule),
  ),
  lazyFeatureRoute(
    ['challenge-types', 'challenge-types/add', 'challenge-types/:id', 'challenge-types/edit/:id'],
    () => import('./lazy/challenge-types.module').then((m) => m.ChallengeTypesFeatureModule),
  ),
  lazyFeatureRoute(
    ['challenges-keywords', 'challenge-keywords/add', 'challenge-keywords/:id', 'challenge-keywords/edit/:id'],
    () => import('./lazy/challenges-keywords.module').then((m) => m.ChallengesKeywordsFeatureModule),
  ),
  lazyFeatureRoute(
    ['challenges', 'challenges/add', 'challenges/addv2', 'challenges/:id', 'challenges/edit/:id', 'challenges/editv2/:id', 'challenges/history/:id', 'challenges-first-build'],
    () => import('./lazy/challenges.module').then((m) => m.ChallengesFeatureModule),
  ),
  lazyFeatureRoute(
    ['climates', 'climates/add', 'climates/:id', 'climates/edit/:id'],
    () => import('./lazy/climates.module').then((m) => m.ClimatesFeatureModule),
  ),
  lazyFeatureRoute(
    ['co-op', 'co-op/add', 'co-op/:id', 'co-op/edit/:id'],
    () => import('./lazy/co-op.module').then((m) => m.CoOpFeatureModule),
  ),
  lazyFeatureRoute(
    ['co-op-release', 'co-op-release/add', 'co-op-release/:id', 'releases/co-op-release/:id', 'co-op-release/edit/:id'],
    () => import('./lazy/co-op-release.module').then((m) => m.CoOpReleaseFeatureModule),
  ),
  lazyFeatureRoute(
    ['collections', 'collections/add', 'collections/:id', 'collections/edit/:id', 'collections/table-view/:id'],
    () => import('./lazy/collections.module').then((m) => m.CollectionsFeatureModule),
  ),
  lazyFeatureRoute(
    ['colors', 'colors/add', 'colors/:id', 'colors/edit/:id'],
    () => import('./lazy/colors.module').then((m) => m.ColorsFeatureModule),
  ),
  lazyFeatureRoute(
    ['items/color-tagging-preview'],
    () => import('./lazy/color-tag-preview.module').then((m) => m.ColorTagPreviewFeatureModule),
  ),
  lazyFeatureRoute(
    ['items/color-tagging/:sessionId'],
    () => import('./lazy/color-tag-review.module').then((m) => m.ColorTagReviewFeatureModule),
  ),
  lazyFeatureRoute(
    ['items/color-tagging/:sessionId/analysis'],
    () => import('./lazy/color-tag-analysis.module').then((m) => m.ColorTagAnalysisFeatureModule),
  ),
  lazyFeatureRoute(
    ['content-schedule', 'content-schedule/add', 'content-schedule/:id', 'content-schedule/edit/:id'],
    () => import('./lazy/content-schedule.module').then((m) => m.ContentScheduleFeatureModule),
  ),
  lazyFeatureRoute(
    ['currencies', 'currencies/add', 'currencies/:id', 'currencies/edit/:id'],
    () => import('./lazy/currencies.module').then((m) => m.CurrenciesFeatureModule),
  ),
  lazyFeatureRoute(
    ['daily-goals', 'daily-goals/add', 'daily-goals/:id', 'daily-goals/edit/:id'],
    () => import('./lazy/daily-goal.module').then((m) => m.DailyGoalFeatureModule),
  ),
  lazyFeatureRoute(
    ['daily-rewards', 'daily-rewards/add', 'daily-rewards/:id', 'daily-rewards/edit/:id'],
    () => import('./lazy/daily-rewards.module').then((m) => m.DailyRewardsFeatureModule),
  ),
  lazyFeatureRoute(
    ['endless-offer', 'endless-offer/add', 'endless-offer/:id', 'releases/endless-offer/:id', 'endless-offer/edit/:id'],
    () => import('./lazy/endless-offer.module').then((m) => m.EndlessOfferFeatureModule),
  ),
  lazyFeatureRoute(
    ['endless-offer-release', 'endless-offer-release/add', 'endless-offer-release/:id', 'releases/endless-offer-release/:id', 'endless-offer-release/edit/:id'],
    () => import('./lazy/endless-offer-release.module').then((m) => m.EndlessOfferReleaseFeatureModule),
  ),
  lazyFeatureRoute(
    ['external-plant-data', 'external-plant-data/add', 'external-plant-data/:id', 'external-plant-data/edit/:id'],
    () => import('./lazy/external-plant-data.module').then((m) => m.ExternalPlantDataFeatureModule),
  ),
  lazyFeatureRoute(
    ['cron-schedules', 'cron-schedules/add', 'cron-schedules/:id', 'cron-schedules/edit/:id'],
    () => import('./lazy/gamedata-schedule.module').then((m) => m.GamedataScheduleFeatureModule),
  ),
  lazyFeatureRoute(
    ['gamedata/diff', 'gamedata/diff/:id'],
    () => import('./lazy/gamedata.module').then((m) => m.GamedataFeatureModule),
  ),
  lazyFeatureRoute(
    ['inbox-messages', 'inbox-messages/add', 'inbox-messages/:id', 'inbox-messages/edit/:id'],
    () => import('./lazy/inbox-messages.module').then((m) => m.InboxMessagesFeatureModule),
  ),
  lazyFeatureRoute(
    ['inbox-templates', 'inbox-templates/add', 'inbox-templates/:id', 'inbox-templates/edit/:id'],
    () => import('./lazy/inbox-templates.module').then((m) => m.InboxTemplatesFeatureModule),
  ),
  lazyFeatureRoute(
    ['infinite-series', 'infinite-series/add', 'infinite-series/:id', 'infinite-series/edit/:id'],
    () => import('./lazy/infinite-series.module').then((m) => m.InfiniteSeriesFeatureModule),
  ),
  lazyFeatureRoute(
    ['item-file-types', 'item-file-types/add', 'item-file-types/:id', 'item-file-types/edit/:id'],
    () => import('./lazy/item-file-types.module').then((m) => m.ItemFileTypesFeatureModule),
  ),
  lazyFeatureRoute(
    ['item-pricing'],
    () => import('./lazy/item-pricing.module').then((m) => m.ItemPricingFeatureModule),
  ),
  lazyFeatureRoute(
    ['item-keywords'],
    () => import('./lazy/item-keywords.module').then((m) => m.ItemKeywordsFeatureModule),
  ),
  lazyFeatureRoute(
    ['item-sets', 'item-sets/add', 'item-sets/:id', 'item-sets/edit/:id'],
    () => import('./lazy/item-sets.module').then((m) => m.ItemSetsFeatureModule),
  ),
  lazyFeatureRoute(
    ['item-types', 'item-types/add', 'item-types/:id', 'item-types/edit/:id'],
    () => import('./lazy/item-types.module').then((m) => m.ItemTypesFeatureModule),
  ),
  lazyFeatureRoute(
    ['items/prizes-automator'],
    () => import('./lazy/prizes-automator.module').then((m) => m.PrizesAutomatorFeatureModule),
  ),
  lazyFeatureRoute(
    ['items', 'items/add', 'items/addv2', 'items/:id', 'items/edit/:id', 'items/editv2/:id', 'items/history/:id'],
    () => import('./lazy/items.module').then((m) => m.ItemsFeatureModule),
  ),
  lazyFeatureRoute(
    ['loading-screens', 'loading-screens/add', 'loading-screens/:id', 'loading-screens/edit/:id'],
    () => import('./lazy/loading-screens.module').then((m) => m.LoadingScreensFeatureModule),
  ),
  lazyFeatureRoute(
    ['materials', 'materials/add', 'materials/:id', 'materials/edit/:id'],
    () => import('./lazy/materials.module').then((m) => m.MaterialsFeatureModule),
  ),
  lazyFeatureRoute(
    ['minigames', 'minigames/add', 'minigames/:id', 'minigames/edit/:id'],
    () => import('./lazy/minigame.module').then((m) => m.MinigameFeatureModule),
  ),
  lazyFeatureRoute(
    ['minigame-types', 'minigame-types/add', 'minigame-types/:id', 'minigame-types/edit/:id'],
    () => import('./lazy/minigame-type.module').then((m) => m.MinigameTypeFeatureModule),
  ),
  lazyFeatureRoute(
    ['mini-game-templates', 'mini-game-templates/add', 'mini-game-templates/:id', 'mini-game-templates/edit/:id'],
    () => import('./lazy/mini-game-template.module').then((m) => m.MiniGameTemplateFeatureModule),
  ),
  lazyFeatureRoute(
    ['miscellaneous-build', 'miscellaneous-build/add', 'miscellaneous-build/:id', 'miscellaneous-build/edit/:id'],
    () => import('./lazy/miscellaneous-build.module').then((m) => m.MiscellaneousBuildFeatureModule),
  ),
  lazyFeatureRoute(
    ['need-categories', 'need-categories/add', 'need-categories/:id', 'need-categories/edit/:id'],
    () => import('./lazy/need-categories.module').then((m) => m.NeedCategoriesFeatureModule),
  ),
  lazyFeatureRoute(
    ['stages', 'stages/add', 'stages/:id', 'stages/edit/:id'],
    () => import('./lazy/stages.module').then((m) => m.StagesFeatureModule),
  ),
  lazyFeatureRoute(
    ['validation-config', 'validation-config/add', 'validation-config/:id', 'validation-config/edit/:id'],
    () => import('./lazy/validation-config.module').then((m) => m.ValidationConfigFeatureModule),
  ),
  lazyFeatureRoute(
    ['creatures', 'creatures/add', 'creatures/:id', 'creatures/edit/:id'],
    () => import('./lazy/creatures.module').then((m) => m.CreaturesFeatureModule),
  ),
  lazyFeatureRoute(
    ['creaturetypes', 'creaturetypes/add', 'creaturetypes/:id', 'creaturetypes/edit/:id'],
    () => import('./lazy/creature-types.module').then((m) => m.CreatureTypesFeatureModule),
  ),
  lazyFeatureRoute(
    ['variants', 'variants/add', 'variants/:id', 'variants/edit/:id'],
    () => import('./lazy/variants.module').then((m) => m.VariantsFeatureModule),
  ),
  lazyFeatureRoute(
    ['animation-triggers', 'animation-triggers/add', 'animation-triggers/:id', 'animation-triggers/edit/:id'],
    () => import('./lazy/animation-triggers.module').then((m) => m.AnimationTriggersFeatureModule),
  ),
  lazyFeatureRoute(
    ['nurture-card', 'nurture-card/add', 'nurture-card/:id', 'nurture-card/edit/:id'],
    () => import('./lazy/nurture-card.module').then((m) => m.NurtureCardFeatureModule),
  ),
  lazyFeatureRoute(
    ['nurture-collection-groups', 'nurture-collection-groups/add', 'nurture-collection-groups/:id', 'nurture-collection-groups/edit/:id'],
    () => import('./lazy/nurture-collection-groups.module').then((m) => m.NurtureCollectionGroupsFeatureModule),
  ),
  lazyFeatureRoute(
    ['nurture-collection', 'nurture-collection/add', 'nurture-collection/:id', 'releases/nurture-collection/:id', 'nurture-collection/edit/:id'],
    () => import('./lazy/nurture-collection.module').then((m) => m.NurtureCollectionFeatureModule),
  ),
  lazyFeatureRoute(
    ['nurture-rarity', 'nurture-rarity/add', 'nurture-rarity/:id', 'nurture-rarity/edit/:id'],
    () => import('./lazy/nurture-rarity.module').then((m) => m.NurtureRarityFeatureModule),
  ),
  lazyFeatureRoute(
    ['nurture-stage', 'nurture-stage/add', 'nurture-stage/:id', 'nurture-stage/edit/:id'],
    () => import('./lazy/nurture-stage.module').then((m) => m.NurtureStageFeatureModule),
  ),
  lazyFeatureRoute(
    ['nurture', 'nurture/add', 'nurture/:id', 'nurture/edit/:id'],
    () => import('./lazy/nurture.module').then((m) => m.NurtureFeatureModule),
  ),
  lazyFeatureRoute(
    ['patterns', 'patterns/add', 'patterns/:id', 'patterns/edit/:id'],
    () => import('./lazy/patterns.module').then((m) => m.PatternsFeatureModule),
  ),
  lazyFeatureRoute(
    ['perks', 'perks/add', 'perks/:id', 'perks/edit/:id'],
    () => import('./lazy/perks.module').then((m) => m.PerksFeatureModule),
  ),
  lazyFeatureRoute(
    ['price-points', 'price-points/add', 'price-points/:id', 'price-points/edit/:id'],
    () => import('./lazy/price-points.module').then((m) => m.PricePointsFeatureModule),
  ),
  lazyFeatureRoute(
    ['progression-levels', 'progression-levels/add', 'progression-levels/:id', 'releases/progression-levels/:id', 'progression-levels/edit/:id'],
    () => import('./lazy/progression-levels.module').then((m) => m.ProgressionLevelsFeatureModule),
  ),
  lazyFeatureRoute(
    ['release-type', 'release-type/add', 'release-type/:id', 'release-type/edit/:id'],
    () => import('./lazy/release-type.module').then((m) => m.ReleaseTypeFeatureModule),
  ),
  lazyFeatureRoute(
    ['releases', 'v2/releases', 'releases/add', 'releases/:id', 'releases/edit/:id'],
    () => import('./lazy/releases.module').then((m) => m.ReleasesFeatureModule),
  ),
  lazyFeatureRoute(
    ['resources', 'resources/add', 'resources/:id', 'resources/edit/:id'],
    () => import('./lazy/resources.module').then((m) => m.ResourcesFeatureModule),
  ),
  lazyFeatureRoute(
    ['restrictions', 'restrictions/add', 'restrictions/:id', 'restrictions/edit/:id'],
    () => import('./lazy/restrictions.module').then((m) => m.RestrictionsFeatureModule),
  ),
  lazyFeatureRoute(
    ['rewards-track', 'rewards-track/add', 'rewards-track/:id', 'rewards-track/edit/:id'],
    () => import('./lazy/rewards-track.module').then((m) => m.RewardsTrackFeatureModule),
  ),
  lazyFeatureRoute(
    ['season-pass', 'season-pass/add', 'season-pass/:id', 'releases/season-pass/:id', 'season-pass/edit/:id'],
    () => import('./lazy/season-pass.module').then((m) => m.SeasonPassFeatureModule),
  ),
  lazyFeatureRoute(
    ['seed-pack-box-types', 'seed-pack-box-types/add', 'seed-pack-box-types/:id', 'seed-pack-box-types/edit/:id'],
    () => import('./lazy/seed-pack-box-types.module').then((m) => m.SeedPackBoxTypesFeatureModule),
  ),
  lazyFeatureRoute(
    ['seed-pack-box', 'seed-pack-box/add', 'seed-pack-box/:id', 'seed-pack-box/edit/:id'],
    () => import('./lazy/seed-pack-box.module').then((m) => m.SeedPackBoxFeatureModule),
  ),
  lazyFeatureRoute(
    ['seed-pack', 'seed-pack/add', 'seed-pack/:id', 'seed-pack/edit/:id'],
    () => import('./lazy/seed-pack.module').then((m) => m.SeedPackFeatureModule),
  ),
  lazyFeatureRoute(
    ['series', 'series/add', 'series/:id', 'releases/series/:id', 'series/edit/:id'],
    () => import('./lazy/series.module').then((m) => m.SeriesFeatureModule),
  ),
  lazyFeatureRoute(
    ['shapes', 'shapes/add', 'shapes/:id', 'shapes/edit/:id'],
    () => import('./lazy/shapes.module').then((m) => m.ShapesFeatureModule),
  ),
  lazyFeatureRoute(
    ['sourcing-challenge-groups', 'sourcing-challenge-groups/add', 'sourcing-challenge-groups/:id', 'sourcing-challenge-groups/edit/:id'],
    () => import('./lazy/sourcing-challenge-groups.module').then((m) => m.SourcingChallengeGroupsFeatureModule),
  ),
  lazyFeatureRoute(
    ['sourcing-groups', 'sourcing-groups/add', 'sourcing-groups/:id', 'sourcing-groups/edit/:id'],
    () => import('./lazy/sourcing-groups.module').then((m) => m.SourcingGroupsFeatureModule),
  ),
  lazyFeatureRoute(
    ['store-listings-v2', 'store-listings-v2/add', 'store-listings-v2/:id', 'store-listings-v2/edit/:id'],
    () => import('./lazy/store-listings-v2.module').then((m) => m.StoreListingsV2FeatureModule),
  ),
  lazyFeatureRoute(
    ['store-sync', 'store-sync/:id'],
    () => import('./lazy/store-sync.module').then((m) => m.StoreSyncFeatureModule),
  ),
  lazyFeatureRoute(
    ['store-transfer', 'store-transfer/:id'],
    () => import('./lazy/store-transfer.module').then((m) => m.StoreTransferFeatureModule),
  ),
  lazyFeatureRoute(
    ['styles', 'styles/add', 'styles/:id', 'styles/edit/:id'],
    () => import('./lazy/styles.module').then((m) => m.StylesFeatureModule),
  ),
  lazyFeatureRoute(
    ['tags', 'tags/add', 'tags/:id', 'tags/edit/:id'],
    () => import('./lazy/tags.module').then((m) => m.TagsFeatureModule),
  ),
  lazyFeatureRoute(
    ['theme-asset', 'theme-asset/add', 'theme-asset/:id', 'theme-asset/edit/:id'],
    () => import('./lazy/theme-asset.module').then((m) => m.ThemeAssetFeatureModule),
  ),
  lazyFeatureRoute(
    ['titles', 'titles/add', 'titles/:id', 'titles/edit/:id'],
    () => import('./lazy/titles.module').then((m) => m.TitlesFeatureModule),
  ),
  lazyFeatureRoute(
    ['traits', 'traits/add', 'traits/:id', 'traits/edit/:id'],
    () => import('./lazy/traits.module').then((m) => m.TraitsFeatureModule),
  ),
  lazyFeatureRoute(
    ['vendors', 'vendors/add', 'vendors/:id', 'vendors/edit/:id'],
    () => import('./lazy/vendors.module').then((m) => m.VendorsFeatureModule),
  ),
];
